/* LA SCENA D'ACCESSO DEL TITOLARE — file suo, movimenti e numeri intatti.
   Dal suo "sonitech-login.jsx" (zip Sonitech_Logo_Animato, 07/10/2026).

   Modifiche, tutte dichiarate qui e nessuna sui movimenti:
     - il logo arriva da /logo-sonitech.jpg invece che da un data-URI di 43 KB;
     - via il pannello delle regolazioni, che vive solo nel suo editor: bagliore e
       tricolore restano accesi come li aveva lasciati (TWEAK_DEFAULTS);
     - il riquadro d'accesso non disegna piu' campi finti con la digitazione simulata:
       al suo posto, nelle stesse identiche coordinate, ci sono i campi VERI.
*/
import React from 'react';
import { CUES as CUE_TAB, Easing, Palco, animate, interpolate, useComposition } from './motore';

const SRC = '/logo-sonitech.jpg';
const BG = '#100F1E';
const CYAN = '#00A8DD';
const WHITE = '#F2F2F5';
const S = 1.6;
const IMG_W = 1024 * S, IMG_H = 196 * S;
const OX = (1920 - IMG_W) / 2, OY = (1080 - IMG_H) / 2;
const LETTERS = [[71,178],[192,315],[337,450],[474,496],[515,622],[637,726],[738,836],[852,954]];
const BOX_TOP = 34, BOX_H = 128;
const K = 3.2; // final symbol: stage px per symbol unit (symbol = 116u)

const MOTION = {
  enter: (start, end) => animate({ from: 0, to: 1, start, end, ease: Easing.easeOutExpo }),
  exit: (start, end) => animate({ from: 0, to: 1, start, end, ease: Easing.easeInCubic }),
  move: (start, end) => animate({ from: 0, to: 1, start, end, ease: Easing.easeInOutCubic }),
  pop: (start, end) => animate({ from: 0, to: 1, start, end, ease: Easing.easeOutBack }),
};
const lerp = (a, b, p) => a + (b - a) * p;

function Letter({ i, x0, x1, T, CUES }) {
  const C = CUES.Trasformazione;
  const inP = MOTION.enter(CUES.Reveal + 0.2 + i * 0.11, CUES.Reveal + 1.2 + i * 0.11)(T);
  const order = i === 0 ? 0 : 8 - i; // H,C,E,T,I,N then S
  const outP = i === 1 ? 0 : MOTION.exit(C + order * 0.045, C + 0.45 + order * 0.045)(T);
  const fadeO = i === 1 ? 1 - MOTION.move(C + 0.25, C + 0.4)(T) : 1;
  const y = (1 - inP) * 105 + outP * 105;
  const L = x0 - 5, W = x1 - x0 + 10;
  return (
    <div style={{ position: 'absolute', left: OX + L * S, top: OY + BOX_TOP * S, width: W * S, height: BOX_H * S, overflow: 'hidden', opacity: fadeO }}>
      <div style={{ position: 'absolute', left: -L * S, top: -BOX_TOP * S, width: IMG_W, height: IMG_H,
        transform: `translateY(${y}%)` }}>
        <img src={SRC} alt="" style={{ position: 'absolute', left: 0, top: 0, width: IMG_W, height: IMG_H, display: 'block' }} />
        {i === 5 && <div style={{ position: 'absolute', left: 635 * S, top: 37 * S, width: 94 * S, height: 28 * S, background: BG }} />}
      </div>
    </div>
  );
}

const FW = 640, FH = 700, FCY = 540;
const BTN = { cy: 760, w: 440, h: 76 };
const clamp = (v) => Math.min(1, Math.max(0, v));

function useExit(T, CUES) {
  return MOTION.exit(CUES.Uscita, CUES.Uscita + 0.8)(T);
}

function Ring({ T, CUES }) {
  const C = CUES.Trasformazione;
  if (T < C + 0.24) return null;
  const p = MOTION.move(C + 0.35, C + 1.35)(T);
  const q = MOTION.move(CUES.Login, CUES.Login + 1.0)(T);
  const out = useExit(T, CUES);
  let w = lerp(124 * S, 116 * K, p), h = lerp(116 * S, 116 * K, p);
  const cx = lerp(OX + 254 * S, 960, p), cy = lerp(OY + 98 * S, 540, p);
  let bx = lerp(25 * S, 20 * K, p), by = lerp(21 * S, 20 * K, p);
  let rx = lerp(40 * S, 40 * K, p), ry = lerp(44 * S, 40 * K, p);
  w = lerp(w, FW, q); h = lerp(h, FH, q);
  bx = lerp(bx, 20, q); by = lerp(by, 20, q);
  rx = lerp(rx, 96, q); ry = lerp(ry, 96, q);
  return (
    <div style={{ position: 'absolute', left: cx - w / 2, top: cy - h / 2, width: w, height: h, boxSizing: 'border-box',
      borderStyle: 'solid', borderColor: WHITE, borderWidth: `${by}px ${bx}px`, borderRadius: `${rx}px / ${ry}px`,
      opacity: 1 - out, transform: `scale(${1 + 0.1 * out})` }} />
  );
}

function Accent({ T, CUES, glow }) {
  const C = CUES.Trasformazione, A = CUES.Accesso;
  const grow = MOTION.pop(CUES.Accento, CUES.Accento + 0.7)(T);
  const travel = MOTION.move(C + 0.45, C + 1.45)(T);
  const land = MOTION.pop(C + 1.3, C + 1.75)(T);
  const q = MOTION.move(CUES.Login + 0.15, CUES.Login + 1.1)(T);
  const out = useExit(T, CUES);
  let w = lerp(88.2 * S, 44 * K, travel), h = lerp(21.2 * S, 20 * K, travel);
  let cx = lerp(OX + 681.7 * S, 960, travel);
  let cy = lerp(OY + 51.2 * S, 540, travel) - Math.sin(travel * Math.PI) * 90;
  w = lerp(w, BTN.w, q); h = lerp(h, BTN.h, q); cy = lerp(cy, BTN.cy, q);
  const squash = T > C + 1.3 && q === 0 ? 1 + 0.12 * Math.sin(Math.min(land, 1) * Math.PI) : 1;
  const press = MOTION.move(A + 2.5, A + 2.65)(T) - MOTION.move(A + 2.65, A + 2.85)(T);
  const flash1 = MOTION.enter(CUES.Accento + 0.3, CUES.Accento + 1.2)(T);
  const flash2 = MOTION.enter(C + 1.35, CUES.Simbolo + 1.2)(T);
  const flash3 = MOTION.enter(A + 2.55, A + 3.2)(T);
  const pulse = (f) => (f > 0 && f < 1 ? Math.sin(f * Math.PI) : 0);
  const g = glow ? (grow > 0 ? 0.35 : 0) + 0.65 * Math.max(pulse(flash1), pulse(flash2), pulse(flash3)) : 0;
  const sx = Math.max(0, grow) * squash * (1 - 0.04 * press);
  const sy = travel > 0 ? (1 / squash) * (1 - 0.04 * press) : 1;
  const label = clamp((T - (CUES.Login + 0.9)) / 0.4);
  return (
    <div style={{ position: 'absolute', left: cx - w / 2, top: cy - h / 2, width: w, height: h, background: CYAN,
      borderRadius: 4 * q, opacity: 1 - out,
      transformOrigin: travel > 0 ? 'center' : 'left center', transform: `scale(${sx * (1 + 0.1 * out)}, ${sy * (1 + 0.1 * out)})`,
      boxShadow: `0 0 ${40 * S}px ${6 * S}px rgba(0,168,221,${0.55 * g * (1 - out)})`,
      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <span style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: 28, letterSpacing: '0.3em', paddingLeft: '0.3em', color: BG, opacity: label }}>ENTRA</span>
    </div>
  );
}

/* I campi VERI prendono il posto di quelli disegnati: stesse coordinate, stessa entrata
   scaglionata. `form` e' una funzione che riceve gli strumenti della scena e restituisce
   i campi dell'applicazione — cosi' la scena resta quella di sempre e l'accesso funziona. */
function LoginForm({ T, CUES, form }) {
  const L = CUES.Login;
  if (T < L + 0.5) return null;
  const out = useExit(T, CUES);
  const st = (k) => MOTION.enter(L + 0.55 + k * 0.1, L + 1.25 + k * 0.1)(T);
  return (
    <div style={{ position: 'absolute', inset: 0, opacity: 1 - out, transform: `scale(${1 + 0.1 * out})`, transformOrigin: '960px 540px' }}>
      <div style={{ position: 'absolute', left: 960 - 180, top: 250 + (1 - st(0)) * 24, width: 360, height: 360 * 196 / 1024, opacity: st(0) }}>
        <img src={SRC} alt="" style={{ width: '100%', height: '100%', display: 'block' }} />
      </div>
      {form && form({ st, larghezza: BTN.w, sinistra: 960 - BTN.w / 2, ciano: CYAN, chiaro: WHITE })}
    </div>
  );
}

function Tricolore({ T, CUES }) {
  const H = CUES.Hold;
  const grow = MOTION.enter(H + 0.05, H + 0.85)(T);
  const shrink = MOTION.exit(H + 1.55, H + 2.2)(T);
  if (grow <= 0 || shrink >= 1) return null;
  const x0 = OX + 71 * S, W = (954 - 71) * S, top = OY + 176 * S, h = 5 * S;
  const cols = ['#009246', '#F1F2F1', '#CE2B37'];
  return (
    <div style={{ position: 'absolute', left: x0, top, width: W, height: h, display: 'flex', gap: 6 * S }}>
      {cols.map((c, k) => {
        const g = Math.min(1, Math.max(0, grow * 3 - k * 0.9));
        const s = Math.min(1, Math.max(0, shrink * 3 - k * 0.9));
        return <div key={k} style={{ flex: 1, height: '100%', background: c, transformOrigin: s > 0 ? 'right center' : 'left center', transform: `scaleX(${s > 0 ? 1 - s : g})` }} />;
      })}
    </div>
  );
}

function SonitechPiece({ form }) {
  const { T, CUES, authoredTotal } = useComposition();
  const t = { glow: true, tricolore: true };   // come i suoi TWEAK_DEFAULTS
  const drift = interpolate([0, authoredTotal], [0.97, 1.02], Easing.linear)(T);
  return (
    <div data-screen-label={`t=${Math.floor(T)}s`} style={{ position: 'absolute', inset: 0, background: BG, overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, transform: `scale(${drift})`, transformOrigin: '50% 50%' }}>
        <Ring T={T} CUES={CUES} />
        {LETTERS.map(([x0, x1], i) => <Letter key={i} i={i} x0={x0} x1={x1} T={T} CUES={CUES} />)}
        <Accent T={T} CUES={CUES} glow={t.glow} />
        {t.tricolore && <Tricolore T={T} CUES={CUES} />}
        <LoginForm T={T} CUES={CUES} form={form} />
      </div>
    </div>
  );
}

export default function SonitechLoginScena({ T, form }) {
  return (
    <Palco T={T} larghezza={1920} altezza={1080} sfondo={BG}>
      <SonitechPiece form={form} />
    </Palco>
  );
}
